import React, { PropsWithChildren, useEffect, useState } from "react";

type CollapsibleProps = PropsWithChildren<{
  open: boolean;
  timingFunction?: React.CSSProperties["transitionTimingFunction"];
  duration?: React.CSSProperties["transitionDuration"];
  renderView?: () => JSX.Element;
}>;

/** allow smooth collapse of wrapped components */
export const Collapsible = ({
  open,
  children,
  duration = "200ms",
  timingFunction = "ease-in-out",
  renderView = () => <div />,
}: CollapsibleProps) => {
  const [size, setSize] = useState<number | "auto">(open ? "auto" : 0);
  const [step, setStep] = useState<0 | 1 | 2 | 3>(open ? 2 : 0);

  useEffect(() => {
    setStep(prev => {
      if (open && prev === 0) return 1;
      if (open && prev === 2) return 3;
      if (!open && prev === 3) return 2;
      if (!open && prev === 2) return 1;
      return prev;
    });
  }, [open, step]);

  const handleTransitionEnd = () => {
    setStep(open ? 2 : 0);
  };

  const handleRef = (target: HTMLDivElement | null) => {
    target && setSize(target?.clientHeight);
    target?.addEventListener("resize", () => setSize(target.clientHeight));
  };

  return (
    <div
      style={{
        transitionProperty: "height",
        transitionTimingFunction: timingFunction,
        transitionDuration: step === 3 ? "0ms" : duration,
        height: step === 3 ? "auto" : step <= 1 && !open ? 0 : size,
        overflow: "hidden",
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      {step > 0 &&
        React.cloneElement(renderView(), {
          children,
          ref: handleRef,
        })}
    </div>
  );
};
